import { alpha, Box, Typography, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import i18n from "i18next";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { useTreeItem } from "@mui/x-tree-view/useTreeItem";
import { PartConfiguratorFormApi } from "src/api/generated/erp/configurator/api/partConfiguratorFormApi.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AavoTreeView } from "src/components/common/tree/AavoTreeView.tsx";
import { flatMapTreeItems } from "src/components/common/tree/AavoTreeView.utils.ts";
import {
	fetchCustomerOfferLinesWithConfiguration,
	SubConfigurationImportSourceOfferLine,
} from "src/components/views/erp/configurator/configuratorForm/impl/customerOfferLineSubConfigurationImportUtils.ts";
import { CustomerOfferLineImportSourceLineList } from "src/components/views/erp/configurator/configuratorForm/impl/CustomerOfferLineImportSourceLineList.tsx";
import {
	getPartConfigurationCurrentInput,
	getPartConfigurationSchema,
	getPartConfigurationTarget,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSchemaClient.ts";
import {
	loadPartConfigurationEvaluator,
	PartConfigurationEvaluation,
	PartConfigurationSchema,
	PartConfigurationSchemaProperty,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import {
	getChildNodes,
	getNode,
	getNodeLabel,
	getNodePartNo,
	getRootNode,
	PropertyInputValues,
	PropertyModelNode,
	PropertyModelNodePath,
	propertyModelNodePathKey,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";
import {
	assignImportTargetProperties,
	canImportPart,
	getTopmostPaths,
	PropertyModelNewChild,
	toImportedChild,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelSubConfigurationImport.ts";

export interface CustomerOfferLinePropertyModelSubConfigurationImportViewProps {
	customerOfferId: number;
	currentCustomerOfferLineId?: number;
	targetSchema: PartConfigurationSchema;
	/** The properties of the target configuration to which children can be added. */
	targetProperties: PartConfigurationSchemaProperty[];
	closeDialog: () => Promise<void>;
	onSubmit: (children: PropertyModelNewChild[]) => Promise<void>;
}

/** The saved property-model configuration of a source line, evaluated by its own schema. */
interface ImportSourceConfiguration {
	schema: PartConfigurationSchema;
	values: PropertyInputValues;
	evaluation: PartConfigurationEvaluation;
}

/**
 * Imports sub-configurations of a property-model form from the saved configurations of the other lines of the offer.
 * A configuration of the source tree can be imported when a property of the target configuration takes children of
 * its part; it is imported with its sub-configurations, and the children get the models of new children.
 */
export function CustomerOfferLinePropertyModelSubConfigurationImportView({
	customerOfferId,
	currentCustomerOfferLineId,
	targetSchema,
	targetProperties,
	closeDialog,
	onSubmit,
}: CustomerOfferLinePropertyModelSubConfigurationImportViewProps) {
	const [selectedLine, setSelectedLine] = useState<SubConfigurationImportSourceOfferLine>();
	const [source, setSource] = useState<ImportSourceConfiguration | null>(null);
	const [selectedPaths, setSelectedPaths] = useState<PropertyModelNodePath[]>([]);

	const assignments =
		source == null ?
			[]
		:	assignImportTargetProperties(
				targetSchema,
				targetProperties,
				selectedPaths.map((path) => ({ path, partNo: getSourcePartNo(source, path) })),
			);
	const allSelectedCanBeImported = assignments.length === selectedPaths.length;

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<AsyncFetchRender
				fetch={() => fetchCustomerOfferLinesWithConfiguration(customerOfferId, currentCustomerOfferLineId)}
				content={(sourceLines) => (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 35%) minmax(0, 1fr)" },
							minHeight: 420,
							flex: 1,
						}}
					>
						<CustomerOfferLineImportSourceLineList
							lines={sourceLines}
							selectedLineId={selectedLine?.customerOrderOfferLineId}
							selectLine={(line) => {
								setSelectedLine(line);
								setSource(null);
								setSelectedPaths([]);
							}}
						/>
						<VerticalBox sx={{ minWidth: 0, overflow: "auto" }}>
							{selectedLine == null ?
								<Typography color="text.secondary" sx={{ paddingX: 2 }}>
									{i18n.t("select_offer_line_first")}
								</Typography>
							:	<AsyncFetchRender
									key={selectedLine.customerOrderOfferLineId}
									fetch={() => fetchSource(selectedLine.customerOrderOfferLineId)}
									content={(fetchedSource) =>
										fetchedSource == null ?
											<Typography color="text.secondary" sx={{ paddingX: 2 }}>
												{i18n.t("offer_line_configuration_cannot_be_imported")}
											</Typography>
										:	<SourceConfigurationTree
												source={fetchedSource}
												targetSchema={targetSchema}
												targetProperties={targetProperties}
												selectedPaths={selectedPaths}
												onSelectionChanged={(paths) => {
													setSource(fetchedSource);
													setSelectedPaths(getTopmostPaths(paths));
												}}
											/>
									}
								/>
							}
							{selectedPaths.length > 0 && !allSelectedCanBeImported && (
								<Typography color="error" sx={{ padding: 2 }}>
									{i18n.t("not_all_selected_sub_configurations_can_be_imported")}
								</Typography>
							)}
						</VerticalBox>
					</Box>
				)}
			/>

			<AavoFormFooter>
				<AsyncButton label={i18n.t("cancel")} color="secondary" onClick={closeDialog} />
				<AsyncButton
					label={i18n.t("import")}
					disabled={source == null || selectedPaths.length === 0 || !allSelectedCanBeImported}
					onClick={async () => {
						if (source == null || assignments.length === 0 || !allSelectedCanBeImported) return;
						await onSubmit(
							assignments.flatMap(({ property, source: { path } }) => {
								const child = toImportedChild(source.values, path);
								return child == null ? [] : [{ property, child }];
							}),
						);
					}}
				/>
			</AavoFormFooter>
		</VerticalBox>
	);
}

/** The saved configuration of the line, or null when it is not on a property-model version. */
async function fetchSource(customerOrderOfferLineId: number): Promise<ImportSourceConfiguration | null> {
	const configuratorType: PartConfiguratorType = {
		type: "customerOrderOfferLine",
		customerOrderOfferLineId: customerOrderOfferLineId,
		explicitPartConfigurationId: null,
		testContextEnabled: false,
	};
	const inputModel = await PartConfiguratorFormApi.getConfiguratorInputModel({ configuratorType });
	const target = getPartConfigurationTarget(configuratorType);
	if (inputModel !== "PROPERTY" || target == null) return null;

	const [schema, currentInput, evaluator] = await Promise.all([
		getPartConfigurationSchema(target),
		getPartConfigurationCurrentInput(target),
		loadPartConfigurationEvaluator(),
	]);
	await evaluator.prepareRuleEngine(schema);
	const values = currentInput.valuesByPropertyName ?? {};
	return { schema, values, evaluation: evaluator.evaluatePartConfiguration(schema, values) };
}

function getSourcePartNo(source: ImportSourceConfiguration, path: PropertyModelNodePath): string | undefined {
	const node = getNode(source.evaluation, path);
	return node == null ? undefined : getNodePartNo(source.schema, node);
}

interface SourceTreeItem {
	id: string;
	label: string;
	isCompatible: boolean;
	path: PropertyModelNodePath;
	children: SourceTreeItem[];
}

interface SourceConfigurationTreeProps extends Pick<
	CustomerOfferLinePropertyModelSubConfigurationImportViewProps,
	"targetSchema" | "targetProperties"
> {
	source: ImportSourceConfiguration;
	selectedPaths: PropertyModelNodePath[];
	onSelectionChanged: (paths: PropertyModelNodePath[]) => void;
}

function SourceConfigurationTree({
	source,
	targetSchema,
	targetProperties,
	selectedPaths,
	onSelectionChanged,
}: SourceConfigurationTreeProps) {
	const treeItems = useMemo(() => {
		const toTreeItem = (node: PropertyModelNode): SourceTreeItem => ({
			id: toTreeItemId(node.path),
			label: getNodeLabel(source.schema, node),
			isCompatible: canImportPart(targetSchema, targetProperties, getNodePartNo(source.schema, node)),
			path: node.path,
			children: getChildNodes(node).flatMap(({ nodes }) => nodes.map(toTreeItem)),
		});
		return [toTreeItem(getRootNode(source.evaluation))];
	}, [source, targetSchema, targetProperties]);
	const [expandedItems, setExpandedItems] = useState(() => flatMapTreeItems(treeItems).map((item) => item.id));

	return (
		<AavoTreeView<SourceTreeItem, true>
			items={treeItems}
			multiSelect
			checkboxSelection
			expandedItems={expandedItems}
			onExpandedItemsChange={setExpandedItems}
			selectedItems={selectedPaths.map(toTreeItemId)}
			onSelectedItemsChange={(_, selectedIds) => {
				const selectedIdSet = new Set(selectedIds);
				onSelectionChanged(
					flatMapTreeItems(treeItems)
						.filter((item) => selectedIdSet.has(item.id))
						.map((item) => item.path),
				);
			}}
			isItemSelectionDisabled={(item) => !item.isCompatible}
			slots={{ item: SourceTreeItemView }}
			hideActionBar
			containerSx={{ paddingX: 1, paddingTop: 1 }}
		/>
	);
}

/** The root's path is empty, so every id gets a prefix. */
function toTreeItemId(path: PropertyModelNodePath): string {
	return `node:${propertyModelNodePathKey(path)}`;
}

const SourceTreeItemView = React.memo(function SourceTreeItemView(props: TreeItemProps) {
	const theme = useTheme();
	const { publicAPI } = useTreeItem(props);
	const item = publicAPI.getItem(props.itemId) as SourceTreeItem;

	return (
		<TreeItem
			{...props}
			sx={
				item.isCompatible ?
					{
						"& > .MuiTreeItem-content": {
							backgroundColor: alpha(theme.palette.success.main, 0.1),
							"&:hover": { backgroundColor: alpha(theme.palette.success.main, 0.16) },
							"&.Mui-selected, &.Mui-selected:hover": {
								backgroundColor: alpha(theme.palette.success.main, 0.24),
							},
						},
					}
				:	undefined
			}
		/>
	);
});
